/**
 * Lead form retargeting in the wizard: the option "Opened the lead form but did not submit" of the
 * retargeting stage. It is a catalogue audience the general audience list does not return; the server
 * offers it per licence. A chosen option is kept in `salesFunnelSetup.warmAudiences` so the later steps can
 * show it like any other audience, and its id goes to `audienceSelections.retargeting`.
 */

export const WARM_AUDIENCE_ID = '7c1f3a52-94d8-4e6b-b0a7-2f5d8e9c6a41'
export const WARM_AUDIENCE_STAGE = 'retargeting'
export const WARM_AUDIENCE_OBJECTIVE = 'leads'

/** The option as a catalogue audience, for the wizard's audience lists. */
export const WARM_AUDIENCE_OPTION = Object.freeze({
    id: WARM_AUDIENCE_ID,
    name: 'Opened the lead form but did not submit',
    description: 'People who opened one of your lead forms in the last 90 days but did not submit it',
    funnel_stage: WARM_AUDIENCE_STAGE,
    icon: 'TargetIcon',
    lookalike: false,
    ai: false,
    active: false,
    default: false,
})

export const WARM_AUDIENCE_TEXT = {
    title: 'Lead form retargeting',
    description:
        "People who opened one of this licence's lead forms in the last 90 days but did not submit it. Meta's audience expansion is turned off for this ad set, so only these people see it.",
    creationNote:
        'Meta builds this audience when the campaign is published and fills it over time; a new audience is used while Meta is still populating it. If Meta reports it as not usable (for example too small), this ad set is not published and you will see a warning.',
}

/** Whether the option is offered for an objective. */
export const isWarmAudienceObjective = (objective) => String(objective || '').toLowerCase() === WARM_AUDIENCE_OBJECTIVE

/** Whether the wizard data has the option selected. */
export const isWarmAudienceSelected = (salesFunnelData) =>
    (salesFunnelData?.audienceSelections?.[WARM_AUDIENCE_STAGE] || []).includes(WARM_AUDIENCE_ID)

/** Wizard data with the option selected or not. Other selections are kept. */
export const withWarmAudience = (salesFunnelData, selected) => {
    const data = salesFunnelData || {}
    const selections = data.audienceSelections || {}
    const current = (selections[WARM_AUDIENCE_STAGE] || []).filter((id) => id !== WARM_AUDIENCE_ID)
    const rest = { ...data }
    delete rest.warmAudiences
    return {
        ...rest,
        audienceSelections: {
            ...selections,
            [WARM_AUDIENCE_STAGE]: selected ? [...current, WARM_AUDIENCE_ID] : current,
        },
        ...(selected ? { warmAudiences: [WARM_AUDIENCE_OPTION] } : {}),
    }
}

/**
 * Adds the wizard's chosen warm audiences to a catalogue audiences list. Returns the very same array when the
 * wizard has none, so screens behave exactly as before.
 */
export const mergeWarmAudiences = (audiences, warmAudiences) => {
    const extra = Array.isArray(warmAudiences) ? warmAudiences.filter((audience) => audience?.id) : []
    if (extra.length === 0) return audiences
    const base = audiences || []
    const known = new Set(base.map((audience) => audience.id))
    const missing = extra.filter((audience) => !known.has(audience.id))
    return missing.length > 0 ? [...base, ...missing] : audiences
}
